import React from 'react';
import './App.css';
import { WorkoutPage, ExercisesPage, ProgressPage, FoodPage, FeedbackPage, AboutPage } from './components';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginScreen } from './components/auth/LoginScreen';
import { ForgotPasswordScreen } from './components/auth/ForgotPasswordScreen';
import { ResetPasswordScreen } from './components/auth/ResetPasswordScreen';
import { apiServices } from './api/services';
import { BaseApiService } from './api/services/BaseApiService';
import AppLayout from './components/layout/AppLayout';
import PublicLayout from './components/layout/PublicLayout';
import BackendHealthCheck from './components/common/BackendHealthCheck';
import StructuredData from './components/seo/StructuredData';
import { I18nProvider, useI18n } from './i18n/provider';
import { HelmetProvider } from 'react-helmet-async';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';

// Main App Content Component
function AppContent() {
  const { isAuthenticated, login } = useAuth();
  const { locale, setLocale } = useI18n();
  const navigate = useNavigate();

  const [selectedExerciseId, setSelectedExerciseId] = React.useState<string | null>(null);
  const [lastViewedTemplate, setLastViewedTemplate] = React.useState<string | null>(() => {
    try {
      return window.localStorage.getItem('last_viewed_template');
    } catch {
      return null;
    }
  });
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | null>(null);

  const handleOpenTemplates = () => {
    navigate('/workouts/templates');
  };

  const handleSwitchToToday = () => {
    navigate('/workouts/today');
  };

  const handleSwitchToHistory = () => {
    navigate('/workouts/history');
  };

  const handleNavigateToExercise = (exerciseId: string, templateId?: string) => {
    setSelectedExerciseId(exerciseId);
    navigate(`/exercises/${encodeURIComponent(exerciseId)}`);
    if (templateId) {
      setLastViewedTemplate(templateId);
      try {
        window.localStorage.setItem('last_viewed_template', templateId);
      } catch {}
    } else {
      // Clear template context if navigating directly to exercise (not from template)
      setLastViewedTemplate(null);
      setSelectedTemplateId(null);
      try {
        window.localStorage.removeItem('last_viewed_template');
      } catch {}
    }
  };

  const handleNavigateToAddExercise = () => {
    setSelectedExerciseId(null);
    navigate('/exercises');
  };

  const handleExerciseAddedToWorkout = () => {
    navigate('/workouts/today');
  };

  const handleBackToTemplate = () => {
    if (lastViewedTemplate) {
      setSelectedTemplateId(lastViewedTemplate);
      navigate('/workouts/templates');
    }
  };

  const handleDemoLogin = async () => {
    try {
      console.log(' [APP] Authenticating demo user through login API');
      
      // Authenticate demo user through the login API to get proper JWT token
      const response = await apiServices.users.login({ 
        email: 'demo@gymplanner.com', 
        password: 'admin' 
      });
      
      console.log(' [APP] Demo user authenticated with JWT token');
      
      // Clear any tracked unauthorized endpoints since user is now authenticated
      BaseApiService.clearAllUnauthorized();
      
      // Store token and user info
      handleLogin(response.user, response.token);
    } catch (error: any) {
      console.error(' [APP] Demo login failed:', error.message);
      
      // Show error to user - demo user not available
      alert('Demo user not available. Please use regular login or contact administrator.');
    }
  };

  const handleLogin = (user: any, token: string) => {
    // Clear any existing local workout session when a user logs in
    apiServices.workoutSession.clearSession();
    
    // Clear template context on fresh login
    setLastViewedTemplate(null);
    setSelectedTemplateId(null);
    
    // Clear any tracked unauthorized endpoints since user is now authenticated
    BaseApiService.clearAllUnauthorized();

    console.log(' [APP] Logging in user with JWT token:', user.id);
    login(user, token);

    if (typeof user?.preferredLocale === 'string' && user.preferredLocale && user.preferredLocale !== locale) {
      void setLocale(user.preferredLocale);
    }
    
    // Redirect to workouts after successful login
    navigate('/workouts/today');
  };

  const WorkoutRouteElement: React.FC = () => {
    const params = useParams();
    const viewRaw = params.view;
    const view = (viewRaw === 'today' || viewRaw === 'history' || viewRaw === 'templates') ? viewRaw : 'today';

    return (
      <WorkoutPage
        view={view}
        onOpenTemplates={handleOpenTemplates}
        onSwitchToToday={handleSwitchToToday}
        onSwitchToHistory={handleSwitchToHistory}
        onNavigateToExercise={handleNavigateToExercise}
        onNavigateToAddExercise={handleNavigateToAddExercise}
        selectedTemplateId={selectedTemplateId}
        onTemplateSelected={setSelectedTemplateId}
      />
    );
  };

  return (
    <div className="app-container">
      <div className="phone-frame">
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/workouts/today" replace />
              ) : (
                <Navigate to="/exercises" replace />
              )
            }
          />

          <Route element={<PublicLayout />}>
            <Route
              path="login"
              element={<LoginScreen onDemoLogin={handleDemoLogin} onLogin={handleLogin} />}
            />
            <Route path="forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="reset-password" element={<ResetPasswordScreen />} />
            <Route
              path="exercises"
              element={
                <ExercisesPage
                  selectedExerciseId={selectedExerciseId}
                  onClearSelection={() => setSelectedExerciseId(null)}
                  onExerciseAddedToWorkout={handleExerciseAddedToWorkout}
                  onBackToTemplate={lastViewedTemplate ? handleBackToTemplate : undefined}
                />
              }
            />
            <Route
              path="exercises/:id"
              element={
                <ExercisesPage
                  selectedExerciseId={selectedExerciseId}
                  onClearSelection={() => setSelectedExerciseId(null)}
                  onExerciseAddedToWorkout={handleExerciseAddedToWorkout}
                  onBackToTemplate={lastViewedTemplate ? handleBackToTemplate : undefined}
                />
              }
            />
          </Route>

          <Route element={<AppLayout />}>
            <Route path="workouts" element={<Navigate to="/workouts/today" replace />} />
            <Route path="workouts/:view" element={<WorkoutRouteElement />} />
            <Route path="progress" element={<ProgressPage />} />
            <Route path="food" element={<FoodPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="api/*" element={null} />
          </Route>

          <Route path="*" element={<Navigate to={isAuthenticated ? '/workouts/today' : '/exercises'} replace />} />
        </Routes>
      </div>
    </div>
  );
}

// Main App Component with Auth Provider
function App() {
  return (
    <HelmetProvider>
      <StructuredData type="WebSite" />
      <I18nProvider>
        <AuthProvider>
          <BackendHealthCheck>
            <AppContent />
          </BackendHealthCheck>
        </AuthProvider>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;